import axios from './axiosConfig'
import type { ApiRequestOptions } from './requestOptions'

export type ProductOptionInput = {
 id?: number
 name: string
 price?: number
 sort_order?: number
 is_active?: boolean
}

export type ProductOptionGroupInput = {
 id?: number
 title: string
 selection_type: 'single' | 'multiple'
 pricing_type: 'free' | 'per_option' | 'group_price'
 price_behavior?: 'additive' | 'defines_base_price'
 group_price?: number | null
 is_required?: boolean
 min_selections?: number | null
 max_selections?: number | null
 sort_order?: number
 is_active?: boolean
 options: ProductOptionInput[]
}

export type InventoryMode = 'stock_product' | 'recipe_product' | 'non_stock'
export type ProductRole = 'sellable_product' | 'raw_material' | 'packaging_material' | 'semi_finished' | 'service'

export type ProductRecipeInput = {
 id?: number
 variant_id?: string | null
 modifier_option_id?: number | null
 ingredient_product_id: number
 quantity: number
 unit_id: number
 waste_percentage?: number
 warehouse_id?: string | null
 is_active?: boolean
 ingredient_product?: {
 id: number
 name: string
 avg_cost?: number | string | null
 cost_price?: number | string | null
 units?: Array<{ id: number; name: string; factor_to_base?: number | string; is_base?: boolean }>
 }
 unit?: { id: number; name: string; factor_to_base?: number | string; is_base?: boolean }
}

export interface ProductData {
 name: string
 barcode?: string
 barcodes?: string[]
 description?: string
 category_id?: number | null
 cost_price: number
 selling_price: number
 units: Array<{
 id?: number
 name: string
 factor_to_base: number
 is_base: boolean
 barcode?: string
 }>
 opening_stock?: Array<{
 warehouse_id: string
 quantity: number
 /** reference into `units[]` */
 unit_index?: number
 }>
 min_stock_alert: number
 inventory_mode?: InventoryMode
 product_role?: ProductRole
 is_sellable?: boolean
 is_purchasable?: boolean
 is_recipe_ingredient?: boolean
 /** افتراضياً true */
 track_inventory?: boolean
 /** تتبع الدفعات وتواريخ الصلاحية */
 track_expiry?: boolean
 track_batch?: boolean
 preferred_supplier_id?: number | null
 default_warehouse_id?: string | null
 storage_type?: string | null
 default_shelf_life_days?: number | null
 specs?: Record<string, string | number | null | undefined>
 /** صورة اختيارية عند الإنشاء/التعديل */
 image?: File | null
 active?: boolean
 featured?: boolean
 is_promotional?: boolean
 promotional_price?: number
 promotional_start_date?: string
 promotional_end_date?: string
 option_groups?: ProductOptionGroupInput[]
 recipes?: ProductRecipeInput[]
}

function appendOptionGroupsToFormData(
 formData: FormData,
 groups?: ProductOptionGroupInput[],
): void {
 if (!Array.isArray(groups)) return
 groups.forEach((g, gi) => {
 if (g.id != null) formData.append(`option_groups[${gi}][id]`, String(g.id))
 formData.append(`option_groups[${gi}][title]`, g.title)
 formData.append(`option_groups[${gi}][selection_type]`, g.selection_type)
 formData.append(`option_groups[${gi}][pricing_type]`, g.pricing_type)
 if (g.price_behavior != null) {
 formData.append(`option_groups[${gi}][price_behavior]`, g.price_behavior)
 }
 if (g.pricing_type === 'group_price' && g.group_price != null && g.group_price !== undefined) {
 formData.append(`option_groups[${gi}][group_price]`, String(g.group_price))
 }
 formData.append(
 `option_groups[${gi}][is_required]`,
 g.is_required === true ? '1' : '0',
 )
 if (g.min_selections != null && g.min_selections !== undefined) {
 formData.append(`option_groups[${gi}][min_selections]`, String(g.min_selections))
 }
 if (g.max_selections != null && g.max_selections !== undefined) {
 formData.append(`option_groups[${gi}][max_selections]`, String(g.max_selections))
 }
 formData.append(`option_groups[${gi}][sort_order]`, String(g.sort_order ?? gi))
 formData.append(
 `option_groups[${gi}][is_active]`,
 g.is_active !== false ? '1' : '0',
 )
 g.options.forEach((o, oi) => {
 if (o.id != null) formData.append(`option_groups[${gi}][options][${oi}][id]`, String(o.id))
 formData.append(`option_groups[${gi}][options][${oi}][name]`, o.name)
 formData.append(`option_groups[${gi}][options][${oi}][price]`, String(o.price ?? 0))
 formData.append(
 `option_groups[${gi}][options][${oi}][sort_order]`,
 String(o.sort_order ?? oi),
 )
 formData.append(
 `option_groups[${gi}][options][${oi}][is_active]`,
 o.is_active !== false ? '1' : '0',
 )
 })
 })
}

function appendRecipesToFormData(formData: FormData, recipes?: ProductRecipeInput[]): void {
 if (!Array.isArray(recipes)) return
 recipes.forEach((r, idx) => {
 if (r.id != null) formData.append(`recipes[${idx}][id]`, String(r.id))
 if (r.variant_id) formData.append(`recipes[${idx}][variant_id]`, String(r.variant_id))
 if (r.modifier_option_id != null) formData.append(`recipes[${idx}][modifier_option_id]`, String(r.modifier_option_id))
 formData.append(`recipes[${idx}][ingredient_product_id]`, String(r.ingredient_product_id))
 formData.append(`recipes[${idx}][quantity]`, String(r.quantity))
 formData.append(`recipes[${idx}][unit_id]`, String(r.unit_id))
 formData.append(`recipes[${idx}][waste_percentage]`, String(r.waste_percentage ?? 0))
 if (r.warehouse_id) formData.append(`recipes[${idx}][warehouse_id]`, String(r.warehouse_id))
 formData.append(`recipes[${idx}][is_active]`, r.is_active === false ? '0' : '1')
 })
}

function appendProductRoleFields(formData: FormData, data: ProductData, mode: InventoryMode): void {
 const role = data.product_role ?? (mode === 'non_stock' ? 'service' : 'sellable_product')
 formData.append('product_role', role)
 if (data.is_sellable !== undefined) formData.append('is_sellable', data.is_sellable ? '1' : '0')
 if (data.is_purchasable !== undefined) formData.append('is_purchasable', data.is_purchasable ? '1' : '0')
 if (data.is_recipe_ingredient !== undefined) formData.append('is_recipe_ingredient', data.is_recipe_ingredient ? '1' : '0')
 if (data.track_batch !== undefined) formData.append('track_batch', data.track_batch ? '1' : '0')
 if (data.preferred_supplier_id !== undefined) formData.append('preferred_supplier_id', data.preferred_supplier_id == null ? '' : String(data.preferred_supplier_id))
 if (data.default_warehouse_id !== undefined) formData.append('default_warehouse_id', data.default_warehouse_id == null ? '' : String(data.default_warehouse_id))
 if (data.storage_type !== undefined) formData.append('storage_type', data.storage_type ?? '')
 if (data.default_shelf_life_days !== undefined) {
 formData.append('default_shelf_life_days', data.default_shelf_life_days == null ? '' : String(data.default_shelf_life_days))
 }
 if (data.specs && typeof data.specs === 'object') {
 Object.entries(data.specs).forEach(([key, value]) => {
 if (!key || value === null || value === undefined || String(value).trim() === '') return
 formData.append(`specs[${key}]`, String(value))
 })
 }
}

export interface ProductInsightsParams {
 from?: string
 to?: string
 movements_page?: number
 movements_per_page?: number
}

export const productsAPI = {
 getAll: async (params?: any) => {
 const response = await axios.get('/products', { params })
 return response.data
 },

 getById: async (id: number, options?: ApiRequestOptions) => {
 const response = await axios.get(`/products/${id}`, options)
 return response.data
 },

 getInsights: async (id: number | string, params?: ProductInsightsParams) => {
 const response = await axios.get(`/products/${id}/insights`, { params })
 return response.data
 },

 search: async (query: string, params?: Record<string, any>, options?: ApiRequestOptions) => {
 const response = await axios.get('/products/search', { params: { query, ...(params || {}) }, ...options })
 return response.data
 },

 create: async (data: ProductData) => {
 const formData = new FormData()
 formData.append('name', data.name)
 // باركودات متعددة
 const barcodes = Array.isArray(data.barcodes) ? data.barcodes.filter(Boolean) : []
 if (barcodes.length > 0) {
 barcodes.forEach((b) => formData.append('barcodes[]', b))
 formData.append('barcode', barcodes[0])
 } else if (data.barcode) {
 formData.append('barcode', data.barcode)
 }
 if (data.description) formData.append('description', data.description)
 if (data.category_id !== undefined) formData.append('category_id', data.category_id == null ? '' : String(data.category_id))
 formData.append('cost_price', String(data.cost_price))
 formData.append('selling_price', String(data.selling_price))
 const mode = data.inventory_mode ?? (data.track_inventory === false ? 'non_stock' : 'stock_product')
 const tracks = mode === 'stock_product'
	 formData.append('inventory_mode', mode)
	 appendProductRoleFields(formData, data, mode)
	 formData.append('track_inventory', tracks ? '1' : '0')
 formData.append('track_expiry', tracks && data.track_expiry === true ? '1' : '0')
 formData.append('min_stock_alert', String(tracks ? data.min_stock_alert : 0))
 // إرسال active و featured دائماً (حتى لو false)
 formData.append('active', data.active === true ? '1' : '0')
 formData.append('featured', data.featured === true ? '1' : '0')
 formData.append('is_promotional', data.is_promotional === true ? '1' : '0')
 if (data.is_promotional && data.promotional_price) {
 formData.append('promotional_price', String(data.promotional_price))
 }
 if (data.is_promotional && data.promotional_start_date) {
 formData.append('promotional_start_date', data.promotional_start_date)
 }
 if (data.is_promotional && data.promotional_end_date) {
 formData.append('promotional_end_date', data.promotional_end_date)
 }
 if (data.image instanceof File) {
 formData.append('image', data.image)
 }

 data.units.forEach((u, idx) => {
 if (u.id) formData.append(`units[${idx}][id]`, String(u.id))
 formData.append(`units[${idx}][name]`, u.name)
 formData.append(`units[${idx}][factor_to_base]`, String(u.factor_to_base))
 formData.append(`units[${idx}][is_base]`, u.is_base ? '1' : '0')
 if (u.barcode) formData.append(`units[${idx}][barcode]`, u.barcode)
 })

 if (tracks && Array.isArray(data.opening_stock)) {
 data.opening_stock.forEach((row, idx) => {
 formData.append(`opening_stock[${idx}][warehouse_id]`, row.warehouse_id)
 formData.append(`opening_stock[${idx}][quantity]`, String(row.quantity))
 if (row.unit_index !== undefined) {
 formData.append(`opening_stock[${idx}][unit_index]`, String(row.unit_index))
 }
 })
 }

 appendOptionGroupsToFormData(formData, data.option_groups)
 if (mode === 'recipe_product') {
 if (Array.isArray(data.recipes) && data.recipes.length > 0) appendRecipesToFormData(formData, data.recipes)
 else formData.append('recipes_clear', '1')
 }

 const response = await axios.post('/products', formData, {
 headers: { 'Content-Type': 'multipart/form-data' },
 })
 return response.data
 },

 update: async (id: number, data: ProductData) => {
 const formData = new FormData()
 formData.append('_method', 'PUT')
 formData.append('name', data.name)
 const barcodes = Array.isArray(data.barcodes) ? data.barcodes.filter(Boolean) : []
 if (barcodes.length > 0) {
 barcodes.forEach((b) => formData.append('barcodes[]', b))
 formData.append('barcode', barcodes[0])
 } else if (data.barcode) {
 formData.append('barcode', data.barcode)
 }
 if (data.description) formData.append('description', data.description)
 if (data.category_id !== undefined) formData.append('category_id', data.category_id == null ? '' : String(data.category_id))
 formData.append('cost_price', String(data.cost_price))
 formData.append('selling_price', String(data.selling_price))
 const modeU = data.inventory_mode ?? (data.track_inventory === false ? 'non_stock' : 'stock_product')
 const tracksU = modeU === 'stock_product'
	 formData.append('inventory_mode', modeU)
	 appendProductRoleFields(formData, data, modeU)
	 formData.append('track_inventory', tracksU ? '1' : '0')
 formData.append('track_expiry', tracksU && data.track_expiry === true ? '1' : '0')
 formData.append('min_stock_alert', String(tracksU ? data.min_stock_alert : 0))
 // إرسال active و featured دائماً (حتى لو false)
 formData.append('active', data.active === true ? '1' : '0')
 formData.append('featured', data.featured === true ? '1' : '0')
 formData.append('is_promotional', data.is_promotional === true ? '1' : '0')
 if (data.is_promotional && data.promotional_price) {
 formData.append('promotional_price', String(data.promotional_price))
 }
 if (data.is_promotional && data.promotional_start_date) {
 formData.append('promotional_start_date', data.promotional_start_date)
 }
 if (data.is_promotional && data.promotional_end_date) {
 formData.append('promotional_end_date', data.promotional_end_date)
 }
 if (data.image instanceof File) {
 formData.append('image', data.image)
 }

 data.units.forEach((u, idx) => {
 if (u.id) formData.append(`units[${idx}][id]`, String(u.id))
 formData.append(`units[${idx}][name]`, u.name)
 formData.append(`units[${idx}][factor_to_base]`, String(u.factor_to_base))
 formData.append(`units[${idx}][is_base]`, u.is_base ? '1' : '0')
 if (u.barcode) formData.append(`units[${idx}][barcode]`, u.barcode)
 })

 appendOptionGroupsToFormData(formData, data.option_groups)
 if (modeU === 'recipe_product') {
 if (Array.isArray(data.recipes) && data.recipes.length > 0) appendRecipesToFormData(formData, data.recipes)
 else formData.append('recipes_clear', '1')
 }

 const response = await axios.post(`/products/${id}`, formData, {
 headers: { 'Content-Type': 'multipart/form-data' },
 })
 return response.data
 },

 delete: async (id: number) => {
 const response = await axios.delete(`/products/${id}`)
 return response.data
 },

 /**
 * Excel export of products. Sends `export_on_hand=1` so Stock Quantity matches ledger-scoped on-hand
 * (aligned with inventory_balances when the backend honors this flag).
 * Pass through optional query params (e.g. branch_id) without dropping the flag.
 */
 export: async (params?: Record<string, string | number | boolean | null | undefined>) => {
 const response = await axios.get('/products/export', {
 params: { export_on_hand: 1, ...params },
 responseType: 'blob',
 })
 return response.data as Blob
 },

 import: async (file: File) => {
 const formData = new FormData()
 formData.append('file', file)

 const response = await axios.post('/products/import', formData, {
 headers: { 'Content-Type': 'multipart/form-data' },
 })
 return response.data
 },
}

export const rawMaterialsAPI = {
 getAll: async (params?: any) => productsAPI.getAll({ ...(params || {}), scope: 'raw_materials', include_raw_materials: 1 }),
 getById: productsAPI.getById,
 create: async (data: ProductData) => productsAPI.create({
 ...data,
 category_id: null,
 preferred_supplier_id: null,
 default_warehouse_id: null,
 inventory_mode: data.inventory_mode ?? 'stock_product',
 product_role: data.product_role ?? 'raw_material',
 is_sellable: data.is_sellable ?? false,
 is_purchasable: data.is_purchasable ?? true,
 is_recipe_ingredient: data.is_recipe_ingredient ?? true,
 track_inventory: data.track_inventory ?? true,
 selling_price: data.selling_price ?? 0,
 }),
 update: async (id: number, data: ProductData) => productsAPI.update(id, {
 ...data,
 category_id: null,
 preferred_supplier_id: null,
 default_warehouse_id: null,
 inventory_mode: data.inventory_mode ?? 'stock_product',
 product_role: data.product_role ?? 'raw_material',
 is_sellable: data.is_sellable ?? false,
 is_purchasable: data.is_purchasable ?? true,
 is_recipe_ingredient: data.is_recipe_ingredient ?? true,
 track_inventory: data.track_inventory ?? true,
 selling_price: data.selling_price ?? 0,
 }),
 delete: productsAPI.delete,
}
